import { Box, Card, CardContent, CardHeader, Dialog, DialogContent, DialogTitle, ListItem, ListItemText, Tab, Typography, useTheme } from "@mui/material";
import { ApexOptions } from "apexcharts";
import { useMemo, useState } from "react";
import { ShowBase, useShowContext, useTranslate } from "react-admin";
import LoadingPanel from "../../components/LoadingPanel";
import { EmptyContent } from "src/components/empty-content";
import ReactApexChart from "react-apexcharts";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import dayjs from "dayjs";

interface RetentionPercentages {
    week0: number | null;
    week1: number | null;
    week2: number | null;
    week3: number | null;
    week4: number | null;
    week5: number | null;
    week6: number | null;
    week7: number | null;
    week8: number | null;
    week9: number | null;
    week10: number | null;
    week11: number | null;
    week12: number | null;
}

interface Cohort {
    week: 'string';
    size: number;
    retentionPercentages: RetentionPercentages;
    userAddresses: string[];
}

interface RetentionData {
    id: string;
    totalNewUsers: string;
    cohorts: Cohort[];
}

const RetentionHeatmapChart = () => {
    const { record, isLoading } = useShowContext<RetentionData>();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark'
    const [dialogOpen, setDialogOpen] = useState(false);
    const [userAddresses, setUserAddresses] = useState(['']);

    const handleYAxisLabelClick = (userAddresses: string[]) => {
      setUserAddresses(userAddresses);
      setDialogOpen(true);
    };

    const chartData = useMemo(() => {
        if (!record?.cohorts) return { series: [], categories: [] };

        const weeks = ['W0', 'W1', 'W2', 'W3', 'W4', 'W5', 'W6', 'W7', 'W8', 'W9', 'W10', 'W11', 'W12'];

        const series = record.cohorts.map((cohort) => {
            const data = weeks.map((_, index) => {
                const weekKey = `week${index}` as keyof RetentionPercentages;
                const value = cohort.retentionPercentages[weekKey];

                return {
                    x: weeks[index],
                    y: value === null ? -1 : Math.round(value),
                }
            })
            const startDate = dayjs.utc(cohort.week.split('/')[0]).format('MMM-DD');
            const endDate = dayjs.utc(cohort.week.split('/')[1]).format('MMM-DD');
            return {
                name: `${startDate} - ${endDate} || ${cohort.size} users`,
                data: data,
                meta: cohort.week,
            }
        });

        return { series, categories: weeks };
    }, [record])

    const options: ApexOptions = useMemo(
      () => ({
        chart: {
          type: 'heatmap',
          toolbar: { show: true },
          foreColor: isDark ? '#fff' : '#000',
          events: {
            mounted: (chartInstance: any) => {
              const yAxisLabels = chartInstance.el.querySelectorAll('.apexcharts-yaxis text');
              yAxisLabels.forEach((label: SVGTextElement, index: number) => {
                label.style.cursor = 'pointer';
                label.addEventListener('click', () => {
                  if (record?.cohorts[index]) {
                    handleYAxisLabelClick(record.cohorts[index-1]?.userAddresses);
                  }
                });
              });
            },
          },
        },
        colors: ['#008FFB'],
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.5,
            radius: 6,
            useFillColorAsStroke: false,
            colorScale: {
              ranges: [
                { from: -1, to: -1, color: isDark ? '#1c252e' : '#f5f5f5', name: 'N/A' },
                { from: 0, to: 0, color: '#ede9f0', name: '0%' },
                { from: 1, to: 25, color: '#c5bccf', name: '1-25%' },
                { from: 26, to: 50, color: '#9d8eaf', name: '26-50%' },
                { from: 51, to: 75, color: '#75618e', name: '51-75%' },
                { from: 76, to: 100, color: '#594b69', name: '76-100%' },
              ],
            },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (val: number) => {
            if (val === -1) return '';
            return `${val}%`;
          },
          style: {
            fontSize: '11px',
            colors: [
              ({ seriesIndex, dataPointIndex, w }: any) => {
                const value = w.globals.series[seriesIndex][dataPointIndex];
                if (value === -1) return 'transparent';
                if (value <= 50) return '#000000';
                return '#ffffff';
              },
            ] as any,
          },
        },
        stroke: {
          width: 1,
          colors: ['#fff'],
        },
        xaxis: {
          type: 'category',
          categories: chartData.categories,
          position: 'top',
          labels: {
            style: {
              colors: isDark ? '#aaa' : '#666',
            },
          },
        },
        yaxis: {
          reversed: true,
          labels: {
            style: {
              fontSize: '11px',
              colors: isDark ? '#aaa' : '#666',
            },
            align: 'center',
            formatter: (val: number) => {
              const str = String(val);
              const [date, size] = str.split('||');
              return [date, size ?? ''] as any;
            },
          },
        },
        tooltip: { enabled: false },
        legend: {
          show: true,
          position: 'bottom',
          labels: {
            colors: isDark ? '#fff' : '#000',
          },
        },
      }),
      [chartData.categories, isDark, record?.cohorts]
    );

    if (!record || !record.cohorts || record.cohorts.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <EmptyContent />
            </Box>
        );
    }

    return (
      <LoadingPanel isLoading={isLoading}>
        <ReactApexChart options={options} series={chartData.series} type="heatmap" height={750} />
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>User Addresses</DialogTitle>
          <DialogContent>
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              {userAddresses?.map((addr: string, idx: number) => (
                <ListItem
                  key={idx}
                  divider={idx < userAddresses.length - 1}
                  sx={{ px: 0, py: 1.5 }}
                >
                  <ListItemText
                    primary={addr}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: { wordBreak: 'break-all', fontFamily: 'monospace' },
                    }}
                  />
                </ListItem>
              ))}
            </Box>
          </DialogContent>
        </Dialog>
      </LoadingPanel>
    );
}


const UserRetentionChart = () => {
    const [tabActive, setTabActive] = useState('dex')
    const t = useTranslate()

    const handleChangeTab = (_: any, newValue: string) => {
        setTabActive(newValue);
    };

    return (
        <Card>
            <CardHeader
                title={t('module.userInfo.userRetention.title')}
            />
            <CardContent>
                <TabContext value={tabActive}>
                    <TabList
                        onChange={handleChangeTab}
                        sx={{
                            '& .MuiTab-root': {
                                flex: 1,
                                minWidth: 'unset',
                                maxWidth: 'unset',
                            },
                        }}
                    >
                        <Tab label={t('module.userInfo.tabDex')} value="dex" />
                        {/* <Tab label={t('module.userInfo.tabMeme')} value="meme" /> */}
                    </TabList>
                    <TabPanel value="dex">
                        <ShowBase resource="user-management/user-info/retention" id="dex">
                            <RetentionHeatmapChart />
                        </ShowBase>
                    </TabPanel>
                    {/* <TabPanel value="meme">
                        <ShowBase resource="user-management/user-info/retention" id="meme">
                            <RetentionHeatmapChart />
                        </ShowBase>
                    </TabPanel> */}
                </TabContext>
            </CardContent>
        </Card>
    )
}

export default UserRetentionChart;